import React from 'react'
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useAppLocale } from '../../i18n/localeUtils'
import { formatDateYYYYMMDD, formatMoney, formatNumber, formatPercentage } from '../../packages/utils/numberFormatters'

function DateRange({ statsRow, locale }) {
    return (
        <>
            <br />
            <Typography variant='caption' color='textSecondary'>
                {formatDateYYYYMMDD(statsRow.startDate, { locale })} ={'>'} {formatDateYYYYMMDD(statsRow.endDate, { locale })}
            </Typography>
        </>
    )
}

function TradingStatsRow({ row, timeLabel, totalNumberOfWarrants, currency }) {
    const { t } = useTranslation()
    const { language: locale } = useAppLocale()
    if (!row || !timeLabel) {
        throw new Error('TradingStatsRow: Missing required props')
    }

    const numberOfWarrants = totalNumberOfWarrants !== null ? parseInt(totalNumberOfWarrants) : null
    const numberOfTradedWarrants = row.totalQuantity !== null ? parseInt(row.totalQuantity) : null
    const avgQuantity = row.avgQuantity !== null ? parseFloat(row.avgQuantity) : null
    const vwap = row.vwap !== null ? parseFloat(row.vwap) : null

    return (
        <TableRow>
            <TableCell>
                {timeLabel}
                <br />
                <DateRange statsRow={row} locale={locale} />
            </TableCell>
            <TableCell>
                {avgQuantity !== null && vwap !== null && avgQuantity > 0
                    ? formatMoney(vwap, { locale, minDecimals: 0, maxDecimals: 4 })
                    : t('site.notAvailable')}
            </TableCell>
            <TableCell>
                {numberOfTradedWarrants !== null ? formatNumber(numberOfTradedWarrants, { locale, decimals: 0 }) : '-'}
            </TableCell>
            <TableCell>
                {numberOfTradedWarrants !== null && numberOfWarrants !== null
                    ? formatPercentage((numberOfTradedWarrants / numberOfWarrants) * 100, {
                          locale,
                          decimals: 0,
                      })
                    : '-'}
            </TableCell>
            <TableCell>
                {row.avgTurnover !== null
                    ? formatMoney(row.avgTurnover, {
                          locale,
                          decimals: 0,
                          currencySymbol: currency,
                          divisor: 1000,
                          suffix: 'k',
                      })
                    : '-'}
            </TableCell>
        </TableRow>
    )
}

function TradingStats({ warrant }) {
    const { t } = useTranslation()

    if (!warrant?.tradesData) {
        return null
    }
    return (
        <TableContainer>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell>{t('pages.warrants.tradingStats.columns.time')}</TableCell>
                        <TableCell>{t('pages.warrants.tradingStats.columns.vwap')}</TableCell>
                        <TableCell>{t('pages.warrants.tradingStats.columns.tradedCount')}</TableCell>
                        <TableCell>{t('pages.warrants.tradingStats.columns.tradedShare')}</TableCell>
                        <TableCell>{t('pages.warrants.tradingStats.columns.turnoverPerDay')}</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {warrant.tradesData['warrantTradePeriod'] ? (
                        <TradingStatsRow
                            row={warrant.tradesData['warrantTradePeriod']}
                            totalNumberOfWarrants={warrant.totalNumberOfWarrants}
                            currency={warrant.currency}
                            timeLabel={t('pages.warrants.tradingStats.sinceTradingStarted')}
                        />
                    ) : null}
                    {warrant.tradesData['previous10Days'] ? (
                        <TradingStatsRow
                            row={warrant.tradesData['previous10Days']}
                            totalNumberOfWarrants={warrant.totalNumberOfWarrants}
                            currency={warrant.currency}
                            timeLabel={t('pages.warrants.tradingStats.lastTenDays')}
                        />
                    ) : null}
                    {warrant.tradesData['latestDay'] ? (
                        <TradingStatsRow
                            row={warrant.tradesData['latestDay']}
                            totalNumberOfWarrants={warrant.totalNumberOfWarrants}
                            currency={warrant.currency}
                            timeLabel={t('pages.warrants.tradingStats.latestDay')}
                        />
                    ) : null}
                </TableBody>
            </Table>
        </TableContainer>
    )
}

export default TradingStats
